import type { ColumnType, Selectable, Insertable, Updateable } from "kysely";
import { z } from "zod";

/** Identifier type for users */
export type UsersId = string & { __flavor: "UsersId" };

/**
 * Represents the table public.users
 * @uniqueConstraints {"users_pkey":{"name":"users_pkey","isPrimary":true,"isUnique":true,"columns":[{"name":"id","definition":"id","predicate":null}]},"users_email_unique":{"name":"users_email_unique","isPrimary":false,"isUnique":true,"columns":[{"name":"email","definition":"email","predicate":null}]},"users_email_index":{"name":"users_email_index","isPrimary":false,"isUnique":false,"columns":[{"name":"email","definition":"email","predicate":null}]}}
 */
export default interface UsersTable {
  id: ColumnType<UsersId, UsersId, UsersId>;

  name: ColumnType<string, string, string>;

  email: ColumnType<string, string, string>;

  emailVerified: ColumnType<boolean, boolean | undefined, boolean>;

  image: ColumnType<string | null, string | null, string | null>;

  createdAt: ColumnType<Date, Date | string | undefined, Date | string>;

  updatedAt: ColumnType<Date, Date | string | undefined, Date | string>;

  twoFactorEnabled: ColumnType<boolean | null, boolean | null, boolean | null>;

  role: ColumnType<string | null, string | null, string | null>;

  banned: ColumnType<boolean | null, boolean | null, boolean | null>;

  banReason: ColumnType<string | null, string | null, string | null>;

  banExpires: ColumnType<Date | null, Date | string | null, Date | string | null>;

  lastActiveOrganizationId: ColumnType<string | null, string | null, string | null>;
}

export type Users = Selectable<UsersTable>;

export type NewUsers = Insertable<UsersTable>;

export type UsersUpdate = Updateable<UsersTable>;

export const usersId = z.string() as unknown as z.Schema<UsersId>;

export const users = z.object({
  id: usersId,
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean(),
  image: z.string().nullable(),
  createdAt: z.date(),
  updatedAt: z.date(),
  twoFactorEnabled: z.boolean().nullable(),
  role: z.string().nullable(),
  banned: z.boolean().nullable(),
  banReason: z.string().nullable(),
  banExpires: z.date().nullable(),
  lastActiveOrganizationId: z.string().nullable(),
});

export const usersInitializer = z.object({
  id: usersId,
  name: z.string(),
  email: z.string(),
  emailVerified: z.boolean().optional(),
  image: z.string().optional().nullable(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
  twoFactorEnabled: z.boolean().optional().nullable(),
  role: z.string().optional().nullable(),
  banned: z.boolean().optional().nullable(),
  banReason: z.string().optional().nullable(),
  banExpires: z.date().optional().nullable(),
  lastActiveOrganizationId: z.string().optional().nullable(),
});

export const usersMutator = z.object({
  id: usersId.optional(),
  name: z.string().optional(),
  email: z.string().optional(),
  emailVerified: z.boolean().optional(),
  image: z.string().optional().nullable(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
  twoFactorEnabled: z.boolean().optional().nullable(),
  role: z.string().optional().nullable(),
  banned: z.boolean().optional().nullable(),
  banReason: z.string().optional().nullable(),
  banExpires: z.date().optional().nullable(),
  lastActiveOrganizationId: z.string().optional().nullable(),
});

// ── constraints for Users ─────────────────────────────────
export const UsersConstraints = {
  users_pkey: {
    name: "users_pkey",
    isPrimary: true,
    isUnique: true,
    columns: [{ name: "id", definition: "id", predicate: null }],
  },
  users_email_unique: {
    name: "users_email_unique",
    isPrimary: false,
    isUnique: true,
    columns: [{ name: "email", definition: "email", predicate: null }],
  },
  users_email_index: {
    name: "users_email_index",
    isPrimary: false,
    isUnique: false,
    columns: [{ name: "email", definition: "email", predicate: null }],
  },
} as const;
export type UsersConstraintsType = typeof UsersConstraints;
